<script setup>
  defineProps({
    breadcrumbs: { type: Array, required: false, default: [] },
    sections: Array,
    recommended: Array,
    post: Object,
  });
</script>
<template>
  <main>
    <article class="container">
      <SfBreadcrumbs :breadcrumbs="breadcrumbs" />
      <div class="single-article">

        <div class="wrapper title-wrapper">
          <div class="title-container">
            <header>
              <h1 class="title" v-html="post.title" />
              <p class="subtitle" v-html="post.description_short" />
            </header>
            <div class="article__meta-data">
              <div v-if="post.author?.name" class="article__meta-data-item">
                <IconPersonRolodex />
                <span>
                  {{ $t('Autor') + ': ' }}
                  {{ post.author?.name }}
                </span>
              </div>
              <VisitorLink v-if="post.category" :href="route('web.blog.category.show', { category: post.category })" :title="post.category.title" class="article__meta-data-item">
                <IconLightning />
                <span class="article__meta-data-category">
                  {{ post.category.title }}
                </span>
              </VisitorLink>
              <span v-if="post.date" class="article__meta-data-item">
                <IconCalender />
                <SfDateTime :value="post.date" />
              </span>
            </div>
          </div>
        </div>

        <div class="wrapper article-content">
          <div class=" content-wrapper">
            <div v-if="post.photos.filter(p => p.type === 'banner').length > 0" class="image-wrapper">
              <MediaPicture :photo="post.photos" sizes="1900x720 2x, 950x360" type="banner" />
            </div>
            <div class="text-wrapper">
              <SectionRenderer :sections class="text-wrapper-sections" />
            </div>
          </div>
          <aside>
            <h2 class="recommended-title">{{ $t('Polecamy artykuły') }}</h2>
            <div class="recommended-wrapper">
              <article v-for="article in recommended" class="recommended-article">
                <MediaPicture :photo="article.photo" sizes="210x130 2x, 105x65" class="recommended-article-image" />
                <h3 class="recommended-article-title">
                  <VisitorLink :href="route('web.blog.post.show', { post: article })">
                    {{ article.title }}
                  </VisitorLink>
                </h3>
              </article>
            </div>
          </aside>
        </div>
      </div>

    </article>
  </main>
</template>
<style lang="scss" scoped>
  main {
    overflow: hidden;
    margin-bottom: 100px;
  }

  .single-article {
    display: flex;
    flex-direction: column;
    gap: 2rem;

    .title-wrapper {
      display: flex;
      flex-direction: column;
      gap: 2rem;

      .title-container {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        padding-left: 4rem;

        .title {
          padding-bottom: 20px;
          font-size: 2rem;
          font-weight: var(--font-weight-700);
        }

        .article__meta-data {
          display: flex;
          flex-wrap: wrap;
          gap: 1rem;

          .article__meta-data-item {
            font-size: 0.875rem;
            display: flex;
            gap: 0.5rem;

            .article__meta-data-category {
              overflow: hidden;
              text-overflow: ellipsis;
              display: -webkit-box;
              -webkit-line-clamp: 3;
              -webkit-box-orient: vertical;
            }

            svg {
              height: 21px;
            }
          }

          a {
            transition-property: color;
            transition-duration: var(--transition-primary-duration);
            transition-timing-function: var(--transition-primary-timing-function);

            @media(hover: hover) {
              &:hover {
                color: var(--primary-color-hover);
              }
            }

            &:focus {
              color: var(--primary-color-hover);
            }
          }
        }
      }

    }

    .article-content {
      display: grid;
      grid-template-columns: 1fr 280px;
      gap: 3rem;


      .content-wrapper {
        position: relative;
        display: flex;
        flex-direction: column;
        gap: 2rem;
        font-family: var(--font-family-primary);
        padding: 4rem 4rem 7rem 0;

        &::after {
          position: absolute;
          content: '';
          height: 100%;
          width: 100vw;
          right: 0;
          top: 0;
          bottom: 0;
          background-color: var(--color-light-gray);
          z-index: -1;
        }

        .image-wrapper {
          width: 100%;

          :deep(picture) {
            display: block;
            width: 100%;
            height: 100%;

            img {
              aspect-ratio: 950/360;
              object-fit: cover;
            }
          }
        }

        .text-wrapper {
          :deep(.text-wrapper-sections) {
            --sf-section-renderer-gap: 1rem;

            h1,
            h2,
            h3,
            h4,
            h5,
            h6,
            .section-typography h1,
            .section-typography h2,
            .section-typography h3,
            .section-typography h4,
            .section-typography h5,
            .section-typography h6 {
              font-size: 1rem;
            }

            .section-wrapper{
              padding: 0;
            }
            .text{
              gap: 0.75rem
            }
          }
        }
      }

      aside {
        display: flex;
        flex-direction: column;
        gap: 1.75rem;

        .recommended-title {
          font-size: 1.5rem;
          font-weight: var(--font-weight-700);
        }

        .recommended-wrapper {
          display: flex;
          flex-direction: column;
          gap: 2rem;
        }

        .recommended-article {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;

          :deep(img) {
            width: 105px;
            aspect-ratio: 105/65;
          }

          .recommended-article-title {
            font-size: 1rem;
            font-weight: var(--font-weight-700);

            a {
              transition-property: color;
              transition-duration: var(--transition-primary-duration);
              transition-timing-function: var(--transition-primary-timing-function);

              @media(hover: hover) {
                &:hover {
                  color: var(--primary-color-hover);
                }
              }

              &:focus {
                color: var(--primary-color-hover);
              }
            }
          }
        }
      }
    }
  }

  @media (max-width: 992px) {
    main {
      margin-bottom: 2rem;
    }

    .single-article {
      gap: 1rem;

      .title-wrapper {
        padding: 0 1rem;

        .title-container {
          gap: 0.75rem;
          padding-left: 0;

          .title {
            padding-bottom: 1rem;
            font-size: 1.25rem;
          }

          .article__meta-data {
            gap: 0.5rem;

            .article__meta-data-item {
              font-size: 0.75rem;

              svg {
                height: 1.125rem;
              }
            }
          }
        }
      }

      .article-content {
        grid-template-columns: 1fr;
        gap: 2rem;

        .content-wrapper {
          gap: 1.5rem;
          padding: 1rem;
        }

        aside {
          gap: 1.25rem;
          padding: 0 1rem;

          .recommended-title {
            font-size: 1.5rem;
          }

          .recommended-wrapper {
            gap: 1rem;
          }

          .recommended-article {
            flex-direction: row;
            gap: 0.75rem;

            .recommended-article-title {
              font-size: 0.75rem;
              font-weight: var(--font-weight-500);
            }
          }
        }
      }
    }
  }
</style>
